import { IFinding, IMotionBbox, IScoutAssessment, IZone } from './detection.interface';
import { IAlertDetections } from './alert.model';

export type SpotlyteJobStatus = 'insufficient' | 'unavailable' | 'queued' | 'processing' | 'completed' | 'failed';

export interface IJobResults {
    summaryUrl: string | null;
    chartsUrl: string | null;
    zonesUrl: string | null;
}

export interface ISummary {
    overview: string;
    totalFindings: number;
    sourcesProcessed: number;
    timeRangeSec: [number, number];
    topObjectTypes: string[];
    highestPriority: number;
    alertRate: number;
    topRulesTriggered: string[];
    peakViolationWindow: string | null;
    topScoutActions: string[];
    mostActiveSource: string | null;
}

export interface ICharts {
    objectTypeCounts: Record<string, number>;
    findingsOverTime: { windowStartSec: number; windowEndSec: number; count: number }[];
    ruleBreakdown: Record<string, number>;
    alertVsNonAlert: { alerts: number; nonAlerts: number };
    confidenceStats: { avg: number; min: number; max: number } | null;
    alertDensityPerMinute: { minute: number; count: number }[];
    scoutActionFrequency: Record<string, number>;
    sourceBreakdown: { sourceAlias: string; totalFindings: number; alertFindings: number }[];
}

export type SpotlyteSourceType = 'upload' | 'vms';

export interface ISpotlyteSource {
    id: string;
    organisationId: string;
    createdBy: string;
    sourceType: SpotlyteSourceType;
    filename: string;
    cameraId: string | null;
    vmsStart: string | null;
    vmsEnd: string | null;
    signedUrl: string | null;
    signedReferenceFrameUrl: string | null;
    durationSeconds: number | null;
    creationTimestamp: string;
    deletionTimestamp: string | null;
}

export interface ISpotlyteJob {
    id: string;
    organisationId: string;
    name: string;
    status: SpotlyteJobStatus;
    licenseStatus: SpotlyteJobStatus | null;
    progress: number | null;
    rules: object[];
    jobConfig: object | null;
    createdBy: string;
    completedAt: string | null;
    summaryUrl: string | null;
    chartsUrl: string | null;
    updatedTimestamp: string;
    creationTimestamp: string;
    deletionTimestamp: string | null;
}

export interface IJobSourceLink {
    id: string;
    sourceId: string;
    ruleOverrides: object | null;
}

export interface ISpotlyteJobDetail extends ISpotlyteJob {
    sourceLinks: IJobSourceLink[];
}

export interface IFindingsFilters {
    sourceId: string | null;
    objectType: string | null;
    minConfidence: number | null;
}

export interface IFindingsPage {
    findingsUrl: string | null;
    page: number;
    pageSize: number;
    filters: IFindingsFilters;
}

export interface IFindingsRequest {
    jobId: string;
    sourceId?: string;
    objectType?: string;
    confidenceMin?: number;
    page?: number;
    perPage?: number;
}

export interface ISpotlyteQnaTurn {
    id: string;
    jobId: string;
    createdBy: string;
    turnNumber: number;
    question: string;
    answer: string;
    creationTimestamp: string;
}

export interface IIngestVmsRequest {
    cameraId: string;
    startTime: string;
    endTime: string;
    filename?: string;
}

export function mapIngestVmsRequests(requests: IIngestVmsRequest[]): object {
    return {
        sources: requests.map(r => ({
            camera_id: r.cameraId,
            start_time: r.startTime,
            end_time: r.endTime,
            ...(r.filename && { filename: r.filename }),
        })),
    };
}

export interface IInitiateUploadRequest {
    filename: string;
    contentType: string;
    fileSizeBytes?: number;
}

export interface IInitiateUploadResponse {
    sourceId: string;
    uploadUrl: string;
}

export interface ICompleteUploadRequest {
    sourceId: string;
    fileSizeBytes?: number;
    durationSeconds?: number;
    referenceFrameGcsPath?: string;
}

export interface ICreateJobRequest {
    name: string;
    sourceIds: string[];
    rulesConfig: object[];
    jobConfig?: object;
    perSourceOverrides?: object[];
}

export function mapInitiateUploadRequest(request: IInitiateUploadRequest): object {
    return {
        filename: request.filename,
        content_type: request.contentType,
        file_size_bytes: request.fileSizeBytes,
    };
}

export function mapCompleteUploadRequest(request: ICompleteUploadRequest): object {
    return {
        source_id: request.sourceId,
        file_size_bytes: request.fileSizeBytes,
        duration_seconds: request.durationSeconds,
        reference_frame_gcs_path: request.referenceFrameGcsPath,
    };
}

export function mapCreateJobRequest(request: ICreateJobRequest): object {
    return {
        name: request.name,
        source_ids: request.sourceIds,
        rules_config: request.rulesConfig,
        job_config: request.jobConfig,
        per_source_overrides: request.perSourceOverrides,
    };
}

export function mapFindingsRequest(filters: IFindingsRequest): object {
    return {
        job_id: filters.jobId,
        source_id: filters.sourceId,
        object_type: filters.objectType,
        confidence_min: filters.confidenceMin,
        page: filters.page,
        per_page: filters.perPage,
    };
}

export function mapSpotlyteSource(r: any): ISpotlyteSource {
    return {
        id: r.id,
        organisationId: r.organisation_id,
        createdBy: r.created_by,
        sourceType: r.source_type,
        filename: r.filename,
        cameraId: r.camera_id ?? null,
        vmsStart: r.vms_start ?? null,
        vmsEnd: r.vms_end ?? null,
        signedUrl: r.signed_url ?? null,
        signedReferenceFrameUrl: r.signed_reference_frame_url ?? null,
        durationSeconds: r.duration_seconds ?? null,
        creationTimestamp: r.creation_timestamp,
        deletionTimestamp: r.deletion_timestamp ?? null,
    };
}

export function mapSpotlyteJob(r: any): ISpotlyteJob {
    return {
        id: r.id,
        organisationId: r.organisation_id,
        name: r.name,
        status: r.status,
        licenseStatus: (r.license_status === 'insufficient' || r.license_status === 'unavailable') ? r.license_status : null,
        progress: r.progress ?? null,
        rules: r.rules ?? [],
        jobConfig: r.job_config ?? null,
        createdBy: r.created_by,
        completedAt: r.completed_at ?? null,
        summaryUrl: r.summary_url ?? null,
        chartsUrl: r.charts_url ?? null,
        updatedTimestamp: r.updated_timestamp,
        creationTimestamp: r.creation_timestamp,
        deletionTimestamp: r.deletion_timestamp ?? null,
    };
}

export function mapSpotlyteJobDetail(r: any): ISpotlyteJobDetail {
    return {
        ...mapSpotlyteJob(r),
        sourceLinks: (r.source_links ?? []).map((s: any) => ({
            id: s.id,
            sourceId: s.source_id,
            ruleOverrides: s.rule_overrides ?? null,
        })),
    };
}

export function mapSpotlyteQnaTurn(r: any): ISpotlyteQnaTurn {
    return {
        id: r.id,
        jobId: r.job_id,
        createdBy: r.created_by,
        turnNumber: r.turn_number,
        question: r.question,
        answer: r.answer,
        creationTimestamp: r.creation_timestamp,
    };
}

export function mapFindingsPage(r: any): IFindingsPage {
    return {
        findingsUrl: r.findings_url ?? null,
        page: r.page,
        pageSize: r.page_size,
        filters: {
            sourceId: r.filters?.source_id ?? null,
            objectType: r.filters?.object_type ?? null,
            minConfidence: r.filters?.min_confidence ?? null,
        },
    };
}

export function mapJobResults(r: any): IJobResults {
    return {
        summaryUrl: r.summary_url ?? null,
        chartsUrl: r.charts_url ?? null,
        zonesUrl: r.zones_url ?? null,
    };
}

export function mapZones(r: any): IZone[] {
    return Array.isArray(r) ? r.map((z: any) => ({
        ruleId: z.rule_id,
        name: z.name,
        polygon: z.polygon ?? [],
    })) : [];
}

export function mapSummary(r: any): ISummary {
    return {
        overview: r.overview ?? '',
        totalFindings: r.total_findings ?? 0,
        sourcesProcessed: r.sources_processed ?? 0,
        timeRangeSec: r.time_range_sec ?? [0, 0],
        topObjectTypes: r.top_object_types ?? [],
        highestPriority: r.highest_priority ?? 0,
        alertRate: r.alert_rate ?? 0,
        topRulesTriggered: r.top_rules_triggered ?? [],
        peakViolationWindow: r.peak_violation_window ?? null,
        topScoutActions: r.top_scout_actions ?? [],
        mostActiveSource: r.most_active_source ?? null,
    };
}

export function mapCharts(r: any): ICharts {
    const ruleBreakdownRaw: Record<string, any> = r.rule_breakdown ?? {};
    const ruleBreakdown: Record<string, number> = {};
    for (const [id, val] of Object.entries(ruleBreakdownRaw)) {
        const key = typeof val === 'object' && val !== null ? (val as any).name ?? id : id;
        const count = typeof val === 'object' && val !== null ? (val as any).alert_count ?? 0 : (val as number);
        ruleBreakdown[key] = count;
    }

    const sourceBreakdownRaw = typeof r.source_breakdown === 'object' && !Array.isArray(r.source_breakdown)
        ? r.source_breakdown ?? {}
        : {};
    const sourceBreakdown = Object.entries(sourceBreakdownRaw).map(([alias, s]: [string, any]) => ({
        sourceAlias: alias,
        totalFindings: s.count ?? 0,
        alertFindings: s.alert_count ?? 0,
    }));

    const avn = r.alert_vs_non_alert;
    return {
        objectTypeCounts: r.object_type_counts ?? {},
        findingsOverTime: (r.findings_over_time ?? []).map((w: any) => ({
            windowStartSec: w.window_start_sec,
            windowEndSec: w.window_end_sec,
            count: w.count,
        })),
        ruleBreakdown,
        alertVsNonAlert: avn
            ? { alerts: avn.alert_count ?? avn.alerts ?? 0, nonAlerts: avn.non_alert_count ?? avn.non_alerts ?? 0 }
            : { alerts: 0, nonAlerts: 0 },
        confidenceStats: r.confidence_stats
            ? { avg: r.confidence_stats.avg ?? 0, min: r.confidence_stats.min ?? 0, max: r.confidence_stats.max ?? 0 }
            : null,
        alertDensityPerMinute: Array.isArray(r.alert_density_per_minute)
            ? r.alert_density_per_minute.map((w: any) => ({ minute: w.minute, count: w.count }))
            : [],
        scoutActionFrequency: r.scout_action_frequency ?? {},
        sourceBreakdown,
    };
}

export function mapFinding(r: any): IFinding {
    return {
        id: r.id,
        sourceId: r.source_id,
        timestampSec: r.timestamp_sec,
        frameNumber: r.frame_number,
        // Wire detections already arrive as the flat snake_case IAlertDetections shape.
        detections: (r.detections ?? {}) as IAlertDetections,
        ruleIds: r.rule_ids ?? [],
        isAlert: r.is_alert ?? false,
        motionBboxes: Array.isArray(r.motion_bboxes)
            ? r.motion_bboxes.map((b: any) => ({ x: b.x, y: b.y, width: b.w, height: b.h, area: b.area ?? 0 }))
            : null,
        scoutAssessment: r.scout_assessment
            ? {
                priority: r.scout_assessment.priority,
                comment: r.scout_assessment.comment,
                actions: r.scout_assessment.actions ?? [],
            }
            : null,
    };
}

export interface IFindingsFilter {
    objectTypes: string[] | null;  // null = no filter; [] = none selected (hide all object findings)
    minConfidence: number;
    alertsOnly: boolean;
}

export interface IPlaybackSync {
    play: boolean;
    time: number;
}

export function applyFindingsFilter(findings: IFinding[], filter: IFindingsFilter): IFinding[] {
    const filtered = findings.filter(f => {
        if (filter.alertsOnly && !f.isAlert) {
            return false;
        }
        const detections = Object.values(f.detections);
        if (filter.objectTypes !== null && detections.length > 0) {
            const objs = new Set(detections.map(d => d.object).filter((o): o is string => typeof o === 'string'));
            if (!filter.objectTypes.some(t => objs.has(t))) {
                return false;
            }
        }
        if (filter.minConfidence > 0 && detections.length > 0) {
            if (!detections.some(d => Number(d.confidence) >= filter.minConfidence)) {
                return false;
            }
        }
        return true;
    });

    if (filter.objectTypes === null && filter.minConfidence === 0) {
        return filtered;
    }

    return filtered.map(f => {
        let entries = Object.entries(f.detections);
        if (filter.objectTypes !== null) {
            entries = entries.filter(([, d]) => typeof d.object === 'string' && filter.objectTypes!.includes(d.object));
        }
        if (filter.minConfidence > 0) {
            entries = entries.filter(([, d]) => Number(d.confidence) >= filter.minConfidence);
        }
        if (entries.length === Object.keys(f.detections).length) {
            return f;
        }
        return { ...f, detections: Object.fromEntries(entries) };
    });
}
